import React, { useState } from 'react';
import { Card, Badge, Button, ProgressBar } from './Shared';
import SafeIcon from '../common/SafeIcon';
import { workflowSteps, shipments } from '../data/mockData';
import { motion, AnimatePresence } from 'framer-motion';

const DemoPage = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [sampleFilter, setSampleFilter] = useState('All');
  const [simulatedState, setSimulatedState] = useState('success');

  const filteredSamples = sampleFilter === 'All' 
    ? shipments 
    : shipments.filter(s => s.status === sampleFilter);

  return (
    <div className="space-y-10 pb-20">
      {/* Header Section */}
      <section className="relative overflow-hidden p-8 rounded-3xl bg-navy-800/50 border border-navy-700">
        <div className="relative z-10 max-w-3xl">
          <Badge type="info" className="mb-4">SYSTEM WALKTHROUGH</Badge>
          <h1 className="text-4xl font-bold text-white mb-4">Demo / How It Works</h1>
          <p className="text-lg text-slate-400 leading-relaxed">
            FreightFlow is a unified workspace for high-volume logistics teams. 
            By centralizing carrier data, customs documents, and real-time tracking, 
            coordinators reduce manual touchpoints by 40%.
          </p>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 blur-[80px] pointer-events-none"></div>
      </section>

      {/* Try this flow Section */}
      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/20 text-orange-500">
            <SafeIcon name="Zap" />
          </div>
          <h2 className="text-2xl font-bold text-slate-100">Try this flow</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Workflow Steps List */}
          <div className="lg:col-span-4 space-y-2">
            {workflowSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`w-full text-left p-4 rounded-xl transition-all border ${
                  activeStep === step.id 
                    ? 'bg-navy-700 border-orange-500/50 shadow-lg glow-orange' 
                    : 'bg-navy-800/40 border-navy-700 hover:border-navy-600'
                }`}
              >
                <div className="flex items-center gap-3 mb-1">
                  <SafeIcon name={step.icon} className={activeStep === step.id ? 'text-orange-500' : 'text-slate-500'} />
                  <span className={`font-bold font-mono text-xs uppercase tracking-wider ${activeStep === step.id ? 'text-orange-400' : 'text-slate-500'}`}>
                    STEP 0{step.id}
                  </span>
                </div>
                <h4 className={`font-semibold ${activeStep === step.id ? 'text-white' : 'text-slate-300'}`}>{step.title}</h4>
                <p className="text-xs text-slate-500 mt-1 line-clamp-1">{step.description}</p>
              </button>
            ))}
          </div>

          {/* Interactive Simulation Panel */}
          <Card className="lg:col-span-8 flex flex-col min-h-[400px] border-orange-500/20 bg-navy-900/40 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="relative z-10 flex flex-col h-full"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <SafeIcon name={workflowSteps.find(s => s.id === activeStep).icon} className="text-orange-500" />
                    {workflowSteps.find(s => s.id === activeStep).module}
                  </h3>
                  <Badge type="info">Active Preview</Badge>
                </div>

                <div className="flex-grow bg-navy-900 border border-navy-700 rounded-xl p-6 flex flex-col items-center justify-center text-center">
                  {/* Simulated Module Content */}
                  <div className="w-full max-w-md space-y-6">
                    <div className="p-4 bg-navy-800 rounded-lg border border-navy-600 text-left">
                      <p className="text-xs font-mono text-slate-500 mb-2 uppercase tracking-widest">Simulated Console</p>
                      <p className="text-sm text-slate-300 font-mono">
                        {`Executing workflow step: ${workflowSteps.find(s => s.id === activeStep).title}...`}
                      </p>
                      <p className="text-xs text-green-500 font-mono mt-2 animate-pulse">
                        &gt; System parameters initialized.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <p className="text-slate-400 text-sm">
                        {workflowSteps.find(s => s.id === activeStep).description}
                      </p>
                      <div className="flex justify-center gap-3">
                         <Button variant="primary" onClick={() => setActiveStep(prev => prev < 7 ? prev + 1 : 1)}>
                           Continue Flow <SafeIcon name="ArrowRight" />
                         </Button>
                         <Button variant="secondary" onClick={() => setActiveStep(1)}>
                           Reset Simulation
                         </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </Card>
        </div>
      </section>

      {/* Sample Data Section */}
      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-500">
            <SafeIcon name="Database" />
          </div>
          <h2 className="text-2xl font-bold text-slate-100">Sample Data</h2>
        </div>

        <Card noPadding>
          <div className="p-4 border-b border-navy-700 flex justify-between items-center bg-navy-900/30">
            <div className="flex gap-2">
              {['All', 'In Transit', 'Exception', 'Delivered'].map(f => (
                <button
                  key={f}
                  onClick={() => setSampleFilter(f)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    sampleFilter === f ? 'bg-navy-700 text-cyan-400 border border-navy-600' : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <span className="text-xs text-slate-500 italic">Showing {filteredSamples.length} simulated records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-navy-900/50 text-[10px] uppercase tracking-widest text-slate-500 border-b border-navy-700">
                  <th className="p-4">Reference</th>
                  <th className="p-4">Carrier</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-700">
                {filteredSamples.map((s, i) => (
                  <tr key={i} className="hover:bg-navy-800/30 transition-colors">
                    <td className="p-4 font-mono text-xs text-slate-300">{s.id}</td>
                    <td className="p-4 text-sm text-slate-400">{s.carrier}</td>
                    <td className="p-4">
                      <Badge type={s.status === 'Exception' ? 'danger' : s.status === 'Delivered' ? 'success' : 'info'}>
                        {s.status}
                      </Badge>
                    </td>
                    <td className="p-4 min-w-[120px]">
                      <ProgressBar progress={s.progress} colorClass={s.status === 'Exception' ? 'bg-alert-500' : 'bg-cyan-500'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* Simulated States Section */}
      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-500/20 text-green-500">
            <SafeIcon name="Layers" />
          </div>
          <h2 className="text-2xl font-bold text-slate-100">Simulated States</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div className="flex gap-2 mb-4">
              {['loading', 'success', 'error', 'empty'].map(s => (
                <button 
                  key={s} 
                  onClick={() => setSimulatedState(s)}
                  className={`flex-1 py-2 text-[10px] uppercase font-bold rounded border ${
                    simulatedState === s ? 'border-orange-500 bg-orange-500/10 text-orange-500' : 'border-navy-700 text-slate-500'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <Card className="min-h-[240px] flex flex-col items-center justify-center text-center p-8">
              <AnimatePresence mode="wait">
                {simulatedState === 'loading' && (
                  <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="space-y-4"
                  >
                    <div className="w-12 h-12 border-4 border-navy-700 border-t-orange-500 rounded-full animate-spin mx-auto"></div>
                    <p className="text-sm font-mono text-slate-500">Fetching AIS Data...</p>
                  </motion.div>
                )}

                {simulatedState === 'success' && (
                  <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    <div className="w-12 h-12 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto text-2xl">
                      <SafeIcon name="Check" />
                    </div>
                    <h4 className="text-white font-bold">Operation Successful</h4>
                    <p className="text-xs text-slate-500">Carrier rate secured and locked for 24 hours.</p>
                  </motion.div>
                )}

                {simulatedState === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    <div className="w-12 h-12 bg-alert-500/20 text-alert-500 rounded-full flex items-center justify-center mx-auto text-2xl">
                      <SafeIcon name="AlertCircle" />
                    </div>
                    <h4 className="text-white font-bold">API Timeout</h4>
                    <p className="text-xs text-slate-500">Connection to Port Authority failed. Retrying...</p>
                  </motion.div>
                )}

                {simulatedState === 'empty' && (
                  <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    <div className="w-12 h-12 bg-navy-700 text-slate-500 rounded-full flex items-center justify-center mx-auto text-2xl">
                      <SafeIcon name="Inbox" />
                    </div>
                    <h4 className="text-white font-bold">No Records Found</h4>
                    <p className="text-xs text-slate-500">Try adjusting your search parameters.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </div>

          <Card className="md:col-span-2 bg-navy-900 shadow-inner border-navy-800 border-2">
            <h4 className="text-sm font-bold text-slate-300 mb-4 flex items-center gap-2">
              <SafeIcon name="Info" className="text-cyan-400" />
              Demo Insights
            </h4>
            <div className="space-y-4">
              <p className="text-sm text-slate-400 leading-relaxed">
                This demo workspace utilizes an <span className="text-cyan-400 font-mono">Ocean Navy</span> visual system designed for 24/7 operations. 
                The <span className="text-orange-500 font-mono">Container Orange</span> accent is reserved for primary actions and active states to maintain high visibility without visual fatigue.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-navy-800 rounded-lg">
                  <p className="text-[10px] text-slate-500 font-bold uppercase mb-1">Responsiveness</p>
                  <p className="text-xs text-slate-300">Fluid layouts scale from desktop towers to mobile field tablets.</p>
                </div>
                <div className="p-3 bg-navy-800 rounded-lg">
                  <p className="text-[10px] text-slate-500 font-bold uppercase mb-1">State Mgmt</p>
                  <p className="text-xs text-slate-300">Predictable UI transitions using Framer Motion for premium feel.</p>
                </div>
              </div>
            </div>
            <div className="mt-8 pt-4 border-t border-navy-800 text-right">
              <p className="text-[10px] text-slate-600 font-mono tracking-widest italic">
                Made by LingLink AI and DigLab AI
              </p>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default DemoPage;